import { ANDROID_APPLICATION_ID_PATTERN, PLAY_TRACK_PATTERN, type DistributionRuleWrite, type Me, type Project } from '@hangar/shared';
import type { DistributionRule, StoreApp } from '@/api/endpointsStores';
import type { Can } from '@/lib/permissions';

/**
 * Pure model of an app's Stores tab (P29 phase 1, SPEC §39.4): the Google Play app form and the matrix of which tracks each
 * environment's builds may reach, both for the org's owners and admins and for platform operators (SPEC decision 44). No
 * React, no DOM.
 */

export type ProjectEnvironment = Project['environments'][number];

/* ---------- Who changes what ---------- */

export interface StoresAccess {
  /** Add and change Google Play apps and their keys: the org's owners and admins, as on EAS, and platform operators. */
  manageApps: Can;
  /** Change the track rules: org admins and platform operators (the API's guardrail). */
  editRules: Can;
}

const HIDDEN: Can = { allowed: false, hidden: true };
const ALLOWED: Can = { allowed: true, hidden: false };

/** Mirrors the API (store-apps.service.ts). `me` undefined (loading) offers nothing yet: the buttons appear with the role. */
export function storesAccessOf(me: Me | undefined, projectId: string): StoresAccess {
  if (!me) return { manageApps: HIDDEN, editRules: HIDDEN };
  const operator = me.user?.platformRole === 'operator' && me.user.isExternal === false;
  const external = me.orgRole === 'guest' || me.user?.isExternal === true || me.projects.some((p) => p.projectId === projectId && p.role === 'client');
  const admins = operator || me.scopes.includes('admin') ? ALLOWED : external ? HIDDEN : { allowed: false, hidden: false, reason: 'Requires admin' };
  return { manageApps: admins, editRules: admins };
}

/* ---------- The Google Play app form ---------- */

/** `keyId` is an existing key's id, or UPLOAD_KEY when a new key file is picked. */
export const UPLOAD_KEY = 'upload';

export interface StoreAppFormValues {
  identifier: string;
  keyId: string;
  newKeyName: string;
  isPrivateManaged: boolean;
  managedPublishing: boolean;
}

export const newStoreAppForm = (keys: { id: string }[]): StoreAppFormValues => ({ identifier: '', keyId: keys[0]?.id ?? UPLOAD_KEY, newKeyName: 'Google Play', isPrivateManaged: false, managedPublishing: false });

export const storeAppFormOf = (s: StoreApp): StoreAppFormValues => ({ identifier: s.identifier, keyId: s.storeAccount.credentialId ?? UPLOAD_KEY, newKeyName: 'Google Play', isPrivateManaged: s.isPrivateManaged, managedPublishing: s.managedPublishing });

export type StoreAppField = 'identifier' | 'key';

/** What is wrong with the form, by field. `editing` keeps the package name as it is (it never changes). */
export function storeAppErrors(v: StoreAppFormValues, editing: boolean, file: File | null): Partial<Record<StoreAppField, string>> {
  const errors: Partial<Record<StoreAppField, string>> = {};
  if (!editing && !ANDROID_APPLICATION_ID_PATTERN.test(v.identifier.trim())) errors.identifier = v.identifier.trim() ? 'A package name looks like com.client.app' : 'Enter the app\'s package name';
  if (v.keyId === UPLOAD_KEY) {
    if (!file) errors.key = 'Choose the service account key (.json) Google Cloud downloaded';
    else if (file.size > 16 * 1024) errors.key = 'A service account key is a few KB: this is not one';
    else if (!v.newKeyName.trim()) errors.key = 'Name the key, such as "Google Play · Malible"';
  }
  return errors;
}

/* ---------- Which tracks each environment may reach ---------- */

export interface RuleRow {
  key: string;
  storeAppId: string;
  env: ProjectEnvironment;
  /** The allowed tracks as typed: "internal, alpha". Empty = the environment never submits. */
  tracks: string;
  /** The largest first release on the production track, as a percent; '' = no limit. Testing tracks are never capped. */
  rolloutPercent: string;
}

/** The first-rollout limit is about the production track only: the field shows once a row may reach it (or holds a value). */
export const showsRolloutCap = (r: Pick<RuleRow, 'tracks' | 'rolloutPercent'>): boolean => parseTracks(r.tracks).includes('production') || r.rolloutPercent !== '';

/** One row per store app and live environment, prefilled from the saved rules. */
export function ruleRowsOf(storeApps: StoreApp[], environments: ProjectEnvironment[], rules: DistributionRule[]): RuleRow[] {
  const live = environments.filter((e) => !e.archivedAt);
  return storeApps.flatMap((s) => live.map((env) => {
    const rule = rules.find((r) => r.storeAppId === s.id && r.environmentId === env.id);
    return { key: `${s.id}:${env.id}`, storeAppId: s.id, env, tracks: rule ? rule.allowedTracks.join(', ') : '', rolloutPercent: rule?.maxInitialRollout ? String(Math.round(rule.maxInitialRollout * 100)) : '' };
  }));
}

/**
 * "internal, Alpha , pre-prod" → ['internal', 'alpha', 'pre-prod']: split on commas (not spaces, so a name such as "closed
 * testing" is caught as a wrong id instead of becoming two tracks), lowercased, once each.
 */
export const parseTracks = (text: string): string[] => [...new Set(text.split(/[,\n]+/).map((t) => t.trim().toLowerCase()).filter(Boolean))];

/** The error of each row, by its key. */
export function ruleErrors(rows: RuleRow[]): Record<string, string | undefined> {
  return Object.fromEntries(rows.map((r) => {
    const bad = parseTracks(r.tracks).find((t) => !PLAY_TRACK_PATTERN.test(t) || t.length > 64);
    if (bad) return [r.key, `"${bad}" is not a track id (internal, alpha, beta, production, pre-prod)`];
    if (r.rolloutPercent) {
      const pct = Number(r.rolloutPercent);
      if (!Number.isInteger(pct) || pct < 1 || pct > 100) return [r.key, 'The first rollout limit is 1–100 %'];
      if (!parseTracks(r.tracks).includes('production')) return [r.key, 'The first rollout limit is for the production track: add production or clear the limit'];
    }
    return [r.key, undefined];
  }));
}

/** The PUT body: the rows with tracks (a row without tracks has no rule, so that environment never submits). */
export function rulesBodyOf(rows: RuleRow[]): DistributionRuleWrite[] {
  return rows
    .map((r) => ({ r, tracks: parseTracks(r.tracks) }))
    .filter(({ tracks }) => tracks.length > 0)
    .map(({ r, tracks }) => ({ storeAppId: r.storeAppId, environmentId: r.env.id, allowedTracks: tracks, ...(r.rolloutPercent ? { maxInitialRollout: Number(r.rolloutPercent) / 100 } : {}) }));
}

/** Rows whose values differ from the saved rules (Save stays off until something changed). */
export const rulesChanged = (rows: RuleRow[], saved: RuleRow[]): boolean =>
  rows.some((r) => {
    const s = saved.find((x) => x.key === r.key);
    return !s || parseTracks(s.tracks).join() !== parseTracks(r.tracks).join() || s.rolloutPercent !== r.rolloutPercent;
  });
